// Native Google Maps overlay for the price/cluster pills on the listing map.
//
// Why this exists: google-map-react renders markers as React DOM children and
// only recomputes their pixel positions on its own (throttled) `onChange`,
// while the map tiles pan at 60fps — so the pills visibly lag/jitter behind
// the map while dragging. A native `google.maps.OverlayView` is positioned by
// the Maps engine itself: its DOM lives in a map pane that Google translates in
// lockstep with the tiles, so the pill stays glued to its coordinate with no
// per-frame React work and no jitter.

type MarkerType = "single" | "cluster";

export interface PriceMarkerOptions {
  lat: number;
  lng: number;
  type: MarkerType;
  price: number;
  otherCount?: number;
  active: boolean;
  onClick: () => void;
}

export interface PriceMarkerHandle {
  overlay: any; // google.maps.OverlayView instance
  setActive: (active: boolean) => void;
}

// The OverlayView subclass can only be defined once the Maps API is loaded
// (it extends maps.OverlayView). Cache it per Maps namespace.
let OverlayClass: any = null;

function getOverlayClass(maps: any): any {
  if (OverlayClass) return OverlayClass;

  class PriceOverlay extends maps.OverlayView {
    private position: any;
    private el: HTMLElement;

    constructor(position: any, el: HTMLElement) {
      super();
      this.position = position;
      this.el = el;
    }

    onAdd() {
      this.el.style.position = "absolute";
      // overlayMouseTarget moves with the map and receives DOM events, so the
      // pill both stays glued to its coordinate and remains clickable.
      const panes = this.getPanes();
      panes.overlayMouseTarget.appendChild(this.el);
    }

    draw() {
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(this.position);
      if (point) {
        this.el.style.left = `${point.x}px`;
        this.el.style.top = `${point.y}px`;
      }
    }

    onRemove() {
      if (this.el.parentNode) {
        this.el.parentNode.removeChild(this.el);
      }
    }
  }

  OverlayClass = PriceOverlay;
  return OverlayClass;
}

const POPPINS = "Poppins, sans-serif";
const INTER = "Inter, sans-serif";

// Builds the pill DOM to match the previous React markers exactly
// (AnyPropertyReactComponent / ClusterMarker).
export function createPriceMarker(
  maps: any,
  opts: PriceMarkerOptions
): PriceMarkerHandle {
  const Overlay = getOverlayClass(maps);
  const position = new maps.LatLng(opts.lat, opts.lng);
  const isCluster = opts.type === "cluster";

  const wrap = document.createElement("div");
  wrap.style.transform = "translate(-50%, -50%)";
  wrap.style.cursor = "pointer";
  wrap.style.userSelect = "none";

  const pill = document.createElement("div");
  pill.style.display = "flex";
  pill.style.alignItems = "center";
  pill.style.justifyContent = "center";
  pill.style.padding = "0 10px";
  pill.style.height = "26px";
  pill.style.borderRadius = "20px";
  pill.style.border = "1px solid #d6d6d6";
  pill.style.boxShadow = "0px 2px 2px rgba(51,51,51,0.25)";
  pill.style.whiteSpace = "nowrap";
  pill.style.width = "max-content";

  const inner = document.createElement("span");
  inner.style.display = "flex";
  inner.style.alignItems = "center";
  inner.style.gap = "2px";
  inner.style.lineHeight = "1";

  const euroEl = document.createElement("span");
  euroEl.textContent = "€";
  euroEl.style.fontFamily = INTER;
  euroEl.style.fontWeight = "600";
  euroEl.style.fontSize = "12px";

  const priceEl = document.createElement("span");
  priceEl.textContent = String(opts.price);
  priceEl.style.fontFamily = POPPINS;
  priceEl.style.fontWeight = "600";
  priceEl.style.fontSize = "14px";

  inner.appendChild(euroEl);
  inner.appendChild(priceEl);

  let countEl: HTMLElement | null = null;
  if (isCluster && opts.otherCount) {
    countEl = document.createElement("span");
    countEl.textContent = `+${opts.otherCount}`;
    countEl.style.fontFamily = POPPINS;
    countEl.style.fontWeight = "600";
    countEl.style.fontSize = "14px";
    countEl.style.color = "#ffffff";
    countEl.style.marginLeft = "4px";
    inner.appendChild(countEl);
  }

  pill.appendChild(inner);
  wrap.appendChild(pill);

  let active = opts.active;
  const applyStyle = () => {
    // Cluster pills are always gold/white. Single pills go gold/white when
    // active (hovered from the list), otherwise white with dark text.
    const gold = isCluster || active;
    pill.style.backgroundColor = gold ? "#B88333" : "#ffffff";
    const textColor = gold ? "#ffffff" : "#01152E";
    euroEl.style.color = textColor;
    priceEl.style.color = textColor;
  };
  applyStyle();

  wrap.addEventListener("click", (e) => {
    // Keep the click from also reaching the map (which clears the selection).
    e.stopPropagation();
    opts.onClick();
  });

  const overlay = new Overlay(position, wrap);

  return {
    overlay,
    setActive: (a: boolean) => {
      if (a !== active) {
        active = a;
        applyStyle();
      }
    },
  };
}
